/**
 * The locales we publish, and the one that routes without a prefix.
 *
 * This is the single source of truth. It lives here, in a dependency-free CJS
 * leaf, because both sides of the app need it and they cannot reach the same
 * kinds of module:
 *
 * - Node, at config time: `next-i18next.config.js` (and through it `next.config.js`),
 *   `config/redirects.js`, and the standalone `scripts/*`. These `require()`, so
 *   they cannot read a TS file.
 * - Bundled code: `src/i18n/config.ts`, and through it the client and the edge
 *   middleware. These can import a plain JS module, but must not pull in
 *   `next-i18next.config.js` itself — that file requires `path`, the sprintf
 *   post-processor and the namespace manifest, none of which belong in a client
 *   or edge bundle.
 *
 * So the list sits below both and neither one owns it. Add or remove a locale
 * here and nowhere else; `__tests__/locales.source-of-truth.test.ts` fails if a
 * copy reappears somewhere that could not be derived (the middleware matcher).
 *
 * The `@type {const}` casts are what let TypeScript read these as the literal
 * tuple `["en", ...]` rather than `string[]`, which is what keeps `FAQLocale`
 * and friends a six-member union. Same pattern next-i18next.config.js already
 * used for the same reason.
 *
 * Two lists since the US launch (WHO-5766):
 *
 * - `contentLocales`: the six languages we write content in. Every map keyed by
 *   language (Lokalise folders, FAQ, menus, footer, Strapi `locale:`, the
 *   gateway's `Language`, the search service's `lang`) is keyed by these.
 * - `locales`: the URL prefixes Next routes. The same six, plus `en-us` while
 *   `US_LAUNCH_ENABLED` is on. `en-us` is English content with US prices: it
 *   reads `en` everywhere content is looked up, through `toContentLocale`.
 *
 * `US_LAUNCH_ENABLED` is read at build time: `next.config.js` inlines it into
 * the client and edge bundles, so the server, the browser and the middleware
 * agree on the list. Unset, empty or anything but `1` / `true` = off, and then
 * `locales` is exactly the six, as before.
 */
const contentLocales = /** @type {const} */ ([
  "en",
  "nl",
  "de",
  "fr",
  "es",
  "it",
]);
const defaultLocale = /** @type {const} */ ("en");

/** The US market: `/en-us/...`, hreflang `en-US`, English content, USD prices. */
const US_LOCALE = /** @type {const} */ ("en-us");

const isUsLaunchEnabled = (value = process.env.US_LAUNCH_ENABLED) =>
  value === "1" || value === "true";

/**
 * Typed as including `en-us` whether or not the flag is on, so the compiler
 * makes every map keyed by `Locale` say what it does for `en-us`.
 *
 * @type {ReadonlyArray<(typeof contentLocales)[number] | typeof US_LOCALE>}
 */
const locales = isUsLaunchEnabled()
  ? [...contentLocales, US_LOCALE]
  : [...contentLocales];

/**
 * The content language of a routed locale: `en-us` reads `en`, every other
 * locale is its own. Use it wherever a locale picks content: a map, a file, an
 * API's language argument, a localized path word.
 *
 * @template {string} T
 * @param {T} locale
 * @returns {Exclude<T, typeof US_LOCALE> | (T extends typeof US_LOCALE ? typeof defaultLocale : never)}
 */
const toContentLocale = locale =>
  /** @type {any} */ (locale === US_LOCALE ? defaultLocale : locale);

/**
 * The hreflang value of a routed locale: `en-us` is written `en-US` (region
 * upper case, as Google documents it); the six are their own code.
 *
 * @param {string} locale
 * @returns {string}
 */
const hreflangOf = locale => (locale === US_LOCALE ? "en-US" : locale);

module.exports = {
  contentLocales,
  locales,
  defaultLocale,
  US_LOCALE,
  isUsLaunchEnabled,
  toContentLocale,
  hreflangOf,
};
